/**
 * useCertMode — Phase 4 (2026-07-27).
 *
 * Detects whether the app is currently in "Hotelbeds certification"
 * isolation mode. Cert mode hides every non-Hotelbeds affiliate
 * widget (Booking.com, CruiseDirect, Amazon, Allianz) so the
 * Hotelbeds review team can inspect the app end-to-end without
 * being distracted by competing bookkeepers.
 *
 * Activation paths (any of):
 *   1. URL query param: `?cert=hotelbeds` on any authenticated route.
 *      Persisted to `sessionStorage.hotelbeds_cert_mode = "1"` so
 *      the flag survives client-side navigation until the tab closes.
 *      `?cert=off` clears the flag.
 *   2. Logged-in user with email `hotelbeds-cert@travelbio.com`.
 *
 * Nothing is deleted on the backend — cert mode is a pure
 * client-side render toggle, so a normal user in the same DB never
 * loses their affiliate widgets.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const SS_KEY = "hotelbeds_cert_mode";
const CERT_EMAIL = "hotelbeds-cert@travelbio.com";

function readFlag() {
  try {
    return typeof window !== "undefined" && window.sessionStorage.getItem(SS_KEY) === "1";
  } catch {
    return false;
  }
}

function writeFlag(v) {
  try {
    if (v) window.sessionStorage.setItem(SS_KEY, "1");
    else window.sessionStorage.removeItem(SS_KEY);
  } catch {
    /* private mode — noop */
  }
}

export function useCertMode() {
  const location = useLocation();
  const { user } = useAuth();
  const [flag, setFlag] = useState(readFlag);

  // Parse `?cert=hotelbeds` / `?cert=off` on every route change and
  // rewrite sessionStorage accordingly. Doesn't mutate the URL —
  // downstream navigations keep whichever ?cert value the user chose.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const raw = (params.get("cert") || "").toLowerCase();
    if (raw === "hotelbeds") {
      writeFlag(true);
      setFlag(true);
    } else if (raw === "off") {
      writeFlag(false);
      setFlag(false);
    } else {
      // No override — re-sync from storage so a fresh mount picks it up.
      setFlag(readFlag());
    }
  }, [location.search]);

  // Auto-activate when the cert reviewer account is signed in.
  const userIsCert = Boolean(
    user && typeof user === "object" && (user.email || "").toLowerCase() === CERT_EMAIL,
  );

  return useMemo(() => ({
    active: flag || userIsCert,
    source: userIsCert ? "user" : flag ? "query" : null,
  }), [flag, userIsCert]);
}

export default useCertMode;
